import React, { useEffect, useState } from 'react';
import { coloredButtonStyle } from '@teetsh/app/src/components/Form/ButtonsRadio';
import ButtonGroup from '../ButtonGroup';
import Button from '@teetsh/app/src/components/Buttons/Button';
import AutoSuggestGroup from '@teetsh/app/src/components/Form/AutoSuggestGroup';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

export interface Option<T> {
  label: string;
  value: T;
  color?: string;
}

interface Props<T> {
  onChange: (value: T[]) => void;
  options: Option<T>[];
  vertical?: boolean;
  value: T[];
  canBeEmpty?: boolean;
  className?: string;
  small?: boolean;
  withCustom?: boolean;
  disableAutosuggest?: boolean;
  fill?: boolean;
}

function hasCustomElement<T>(options: Option<T>[], value: T[]) {
  const optionsValues = options.map(opt => opt.value);
  const emptyArr: T[] = [];
  return (value || emptyArr).filter(v => !optionsValues.includes(v)).length > 0;
}

export function Buttons<T = string>(props: Props<T>) {
  const {
    value,
    disableAutosuggest,
    vertical,
    onChange,
    options,
    className = '',
    canBeEmpty,
    small,
    fill,
    withCustom,
  } = props;
  const [inputs, setInputs] = useState<boolean>(
    hasCustomElement(options, value),
  );
  const { t } = useTranslation(['common']);
  useEffect(() => {
    if (value.length === 0) {
      setInputs(false);
    }
  }, [value]);
  if (inputs && !disableAutosuggest) {
    return (
      <AutoSuggestGroup
        options={options as any}
        onChange={items => onChange(items.map(i => (i as any).value))}
        items={(value || []).map((v, i) => ({ value: v, id: i })) as any}
      />
    );
  }
  return (
    <ButtonGroup fill={fill} vertical={vertical} className={className}>
      {options.map(option => {
        const isActive = value.includes(option.value);

        return (
          <Button
            small={small}
            css={[
              coloredButtonStyle({
                color: option.color,
                active: isActive,
              }),
              isActive &&
                !option.color &&
                tw`bg-brand-moderate text-brand-intense`,
            ]}
            active={isActive}
            onClick={() => {
              const isLast = value.length === 1;
              if (isActive && !isLast) {
                return onChange(value.filter(v => v !== option.value));
              }
              if (isActive && isLast) {
                if (canBeEmpty) {
                  return onChange([]);
                }
                return value;
              }
              return onChange([...value, option.value]);
            }}
            key={JSON.stringify(option.value)}
          >
            {option.label}
          </Button>
        );
      })}
      {withCustom && (
        <Button
          onClick={() => {
            onChange([...value, ''] as any);
            setInputs(true);
          }}
        >
          {t('common:buttons.other')}
        </Button>
      )}
    </ButtonGroup>
  );
}
